import { useEffect, useRef, useState } from "react";
import { fetchEditedFrom, fetchPlan, linkEditedFrom } from "@/pages/beta/courseFitApi";
import { readDraft, workingNames } from "@/lib/fastFitGuideContext";

const CAP = 3;

// Restores picks once per result: this tab's working selection > this result's owned saved plan
// > plan of the attempt it was edited from (owned server-side link) > confirmed journey picks > top-1.
// Returns the source used (null until restored, "error" if a saved plan could not be read).
export function useRestoredPicks({ snapshot, session, planFrom, ready, confirmed, fallbackKey, keyFor, setPickedKeys }) {
  const [source, setSource] = useState(null);
  const [editedFrom, setEditedFrom] = useState(null);
  const [planAt, setPlanAt] = useState(null);
  const done = useRef(null);
  const keyRef = useRef(keyFor);
  keyRef.current = keyFor;
  const sid = snapshot?.sessionId;
  const snapId = snapshot?.snapshotId;

  useEffect(() => {
    if (!ready || !sid || done.current === snapId) return undefined;
    let live = true;
    (async () => {
      const token = session?.sessionId === sid ? session?.accessToken : null;
      const link = await fetchEditedFrom(sid, token);
      let linked = link.ok ? link.data.editedFromSessionId : null;
      // The edited attempt is linked server-side only once it has a completed result (owner-checked).
      if (!linked && planFrom && planFrom !== sid && (await linkEditedFrom(sid, token, planFrom)).ok) linked = planFrom;
      const from = linked || planFrom;
      let plan = null;
      let src = null;
      const own = await fetchPlan(sid, token);
      let failed = !own.ok;
      const work = own.ok ? workingNames(readDraft(snapId), own.data.plan) : null;
      if (live) setPlanAt(own.ok ? own.data.plan?.updatedAt || null : null);
      if (work) { plan = { interests: work.names }; src = work.from; }
      else if (own.ok && from && from !== sid) {
        const prev = await fetchPlan(from, null);
        failed = !prev.ok;
        if (prev.ok && prev.data.plan?.interests?.length) { plan = prev.data.plan; src = "earlier-plan"; }
      }
      if (!live) return;
      setEditedFrom(from && from !== sid ? from : null);
      if (failed) { setSource("error"); return; }  // never fall back to a smaller set we could save over the plan
      const conf = (confirmed || []).map((c) => c?.slug || c).filter(Boolean);
      const keys = plan ? plan.interests.map((n) => keyRef.current(n)) : conf.length ? conf : fallbackKey ? [fallbackKey] : [];
      done.current = snapId;
      setPickedKeys(new Set(keys.slice(0, CAP)));
      setSource(plan ? src : conf.length ? "confirmed" : "default");
    })();
    return () => { live = false; };
  }, [ready, sid, snapId]); // eslint-disable-line react-hooks/exhaustive-deps

  return { source, editedFrom, planAt, setPlanAt };
}
